import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  GetApp as DownloadIcon,
  DateRange as DateIcon,
  Store as StoreIcon,
  Filter as FilterIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';
import { isStoreAdmin, isAllStoresAdmin } from '../../utils/roles';
import { useTheme, useMediaQuery } from '@mui/material';

const Reports = () => {
  const { state, actions } = useApp();
  const { user } = state;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Report filters state
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    storeId: 'all',
    orderStatus: 'all'
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [storeDetails, setStoreDetails] = useState(null);
  const [error, setError] = useState('');
  
  // Order status options (matching your existing order system)
  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'returned', label: 'Returned' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    const initializePage = async () => {
      setPageLoading(true);
      
      // Set default date range (yesterday only)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      setFilters(prev => ({
        ...prev,
        startDate: yesterday.toISOString().split('T')[0],
        endDate: yesterday.toISOString().split('T')[0]
      }));

      // Initialize based on user role
      try {
        if (isStoreAdmin(user?.role)) {
          await fetchStoreDetails();
        } else if (isAllStoresAdmin(user?.role)) {
          await fetchStores();
        }
      } catch (error) {
        console.error('Error initializing page:', error);
      } finally {
        setPageLoading(false);
      }
    };

    if (user) {
      initializePage();
    }
  }, [user]);

  const fetchStoreDetails = async () => {
    if (!user?.linkedStore) return;
    
    try {
      const response = await api.get(`/stores/${user.linkedStore}`);
      if (response.data.success) {
        setStoreDetails(response.data.store);
        // For store admin, set the store filter to their store
        setFilters(prev => ({
          ...prev,
          storeId: response.data.store.branchId || response.data.store.id
        }));
      }
    } catch (error) {
      console.error('Error fetching store details:', error);
      setError('Failed to load store information');
    }
  };

  const fetchStores = async () => {
    try {
      const storesData = await cacheEndpoints.stores.getAll();
      setStores(storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
      actions.showSnackbar('Error fetching stores', 'error');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };
      
      // Real-time validation for date range
      if ((field === 'startDate' || field === 'endDate') && newFilters.startDate && newFilters.endDate) {
        const startDate = new Date(newFilters.startDate);
        const endDate = new Date(newFilters.endDate);
        
        if (startDate > endDate) {
          setError('Start date must be before end date');
        } else {
          const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
          if (daysDiff > 31) {
            setError('Date range cannot exceed 1 month (31 days)');
          } else {
            setError(''); // Clear error if validation passes
          }
        }
      }
      
      return newFilters;
    });
  };

  const validateFilters = () => {
    const errors = [];
    
    if (!filters.startDate) {
      errors.push('Start date is required');
    }
    
    if (!filters.endDate) {
      errors.push('End date is required');
    }
    
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      
      if (startDate > endDate) {
        errors.push('Start date must be before end date');
      }
      
      // Check if date range is too large (more than 1 month)
      const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
      if (daysDiff > 31) {
        errors.push('Date range cannot exceed 1 month (31 days)');
      }
    }
    
    return errors;
  };

  const isDateRangeValid = () => {
    if (!filters.startDate || !filters.endDate) return false;
    
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    
    if (startDate > endDate) return false;
    
    const daysDiff = (endDate - startDate) / (1000 * 60 * 60 * 24);
    return daysDiff <= 31;
  };

  const generateSalesReport = async () => {
    const validationErrors = validateFilters();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Build query parameters
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: 'excel'
      };
      
      // Add store filter
      if (filters.storeId !== 'all') {
        if (user?.role === 'store_admin') {
          params.branchId = filters.storeId;
        } else {
          params.storeId = filters.storeId;
        }
      }
      
      // Add status filter
      if (filters.orderStatus !== 'all') {
        params.status = filters.orderStatus;
      }
      
      console.log('Generating sales report with params:', params);
      
      // Make API call to download Excel
      const response = await api.get('/reports/sales/export', {
        params,
        responseType: 'blob' // Important for file download
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const statusSuffix = filters.orderStatus !== 'all' ? `_${filters.orderStatus}` : '';
      const storeSuffix = filters.storeId !== 'all' ? `_store_${filters.storeId}` : '';
      
      link.setAttribute('download', `sales_report_${timestamp}${statusSuffix}${storeSuffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      actions.showSnackbar('Sales report downloaded successfully!', 'success');
      
    } catch (error) {
      console.error('Error generating sales report:', error);
      setError(error.response?.data?.message || 'Failed to generate sales report');
      actions.showSnackbar('Failed to generate sales report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getFilterSummary = () => {
    const summary = [];
    
    if (filters.startDate && filters.endDate) {
      summary.push(`${filters.startDate} to ${filters.endDate}`);
    }
    
    if (filters.storeId !== 'all') {
      const storeName = user?.role === 'store_admin' 
        ? storeDetails?.storeName 
        : stores.find(s => s.branchId === filters.storeId || s.id === filters.storeId)?.storeName;
      summary.push(`Store: ${storeName || filters.storeId}`);
    } else {
      summary.push('All Stores');
    }
    
    if (filters.orderStatus !== 'all') {
      const statusLabel = statusOptions.find(s => s.value === filters.orderStatus)?.label;
      summary.push(`Status: ${statusLabel}`);
    } else {
      summary.push('All Statuses');
    }
    
    return summary;
  };

  // Loading Screen
  if (pageLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '60vh',
          p: 3 
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ mb: 2, color: 'primary.main' }}>
          Loading Sales Report
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center">
          Setting up your reporting dashboard...
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Chip label="Fetching Store Data" size="small" variant="outlined" />
          <Chip label="Initializing Filters" size="small" variant="outlined" />
          <Chip label="Preparing Interface" size="small" variant="outlined" />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ReportsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Sales Report
            </Typography>
          </Box>

      {/* Sales Report Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} />
          Sales Report Filters
        </Typography>
        
        <Divider sx={{ my: 2 }} />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{width:'100%'}}>
          {/* First Row: Date Range (50%) and Store (50%) */}
          
          {/* Date Range - 50% width */}
          <Grid item xs={12} lg={6} sx={{width:'50%'}}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
              <DateIcon color="primary" />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Date Range
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: filters.endDate || new Date().toISOString().split('T')[0]
                }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: filters.startDate,
                  max: filters.startDate ? 
                    new Date(new Date(filters.startDate).getTime() + 31 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
                    new Date().toISOString().split('T')[0]
                }}
                fullWidth
              />
            </Box>
            {/* Date Range Helper Text */}
            {filters.startDate && filters.endDate && (
              <Box sx={{ mt: 1 }}>
                {isDateRangeValid() ? (
                  <Typography variant="caption" color="success.main">
                    ✓ Valid date range: {Math.ceil((new Date(filters.endDate) - new Date(filters.startDate)) / (1000 * 60 * 60 * 24))} days
                  </Typography>
                ) : (
                  <Typography variant="caption" color="error.main">
                    ✗ Date range exceeds 31 days or is invalid
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          {/* Store Selection - 50% width (always shown, disabled for store admin) */}
          <Grid item xs={12} md={6} sx={{width:'40%'}}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <StoreIcon color="primary" />
              <Typography variant="h6">
                Store
              </Typography>
            </Box>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select Store</InputLabel>
              <Select
                value={filters.storeId}
                onChange={(e) => handleFilterChange('storeId', e.target.value)}
                label="Select Store"
                disabled={user?.role === 'store_admin'}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.branchId || store.id}>
                    {store.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {user?.role === 'store_admin' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Store selection is limited to your assigned store
              </Typography>
            )}
          </Grid>

          {/* Second Row: Order Status - Full width */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FilterIcon color="primary" />
              <Typography variant="h6">
                Order Status
              </Typography>
            </Box>
            <FormControl sx={{ mt: 2, width: '300px' }}>
              <InputLabel>Select Status</InputLabel>
              <Select
                value={filters.orderStatus}
                onChange={(e) => handleFilterChange('orderStatus', e.target.value)}
                label="Select Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Filter Summary */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Filters:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {getFilterSummary().map((filter, index) => (
              <Chip
                key={index}
                label={filter}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        {/* Download Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={generateSalesReport}
            disabled={loading || !isDateRangeValid()}
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
            sx={{
              minWidth: 200,
              height: 50,
              fontSize: '1.1rem'
            }}
          >
            {loading ? 'Generating Report...' : 'Download Report'}
          </Button>
        </Box>
      </Paper>

      {/* Instructions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Instructions
        </Typography>
        <Typography variant="body2" paragraph>
          1. Select your desired date range (maximum 1 month)
        </Typography>
        <Typography variant="body2" paragraph>
          2. Choose a specific store or select "All Stores" {user?.role === 'store_admin' && '(limited to your store)'}
        </Typography>
        <Typography variant="body2" paragraph>
          3. Filter by order status or select "All Orders"
        </Typography>
        <Typography variant="body2" paragraph>
          4. Click "Download Report" to generate an Excel file with the order data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The report will include order details, customer information, product data, and financial summaries based on your selected filters.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Reports;
